// Forecast-data layer for SeaState.
//
// Fetches live marine forecast data straight from the browser against the
// public, CORS-enabled Open-Meteo APIs and returns it as typed data. This
// module does NO rendering and touches NO DOM — it is data in, data out.
//
// Every upstream request is independent and degrades gracefully: a dead API
// makes that section `null`, it never throws into a blank page. The only throw
// is the genuinely unusable case where not a single time axis came back.

// The one import: recording fetch outcomes for the Systems board. Still no
// rendering, still no DOM — health is data out, same as everything else here.
import { reportSystem } from "../ops/health.js";

export interface ModelSeries {
  key: string;
  label: string;
  slot: string;
  data: (number | null)[];
}

export interface WindForecast {
  /** wind_speed_10m per model, in KNOTS, aligned to `times`. */
  models: ModelSeries[];
  /** primary model wind_direction_10m ("from" convention). */
  directionDeg: (number | null)[];
  ensemble: {
    p05: (number | null)[];
    p50: (number | null)[];
    p95: (number | null)[];
  } | null;
}

export interface WaveForecast {
  /** wave_height per model, metres. */
  height: ModelSeries[];
  /**
   * wave_period per model, seconds — NOT one quantity across the four.
   *
   * GFS-Waves publishes PEAK period here and the other three publish ENERGY
   * period; each series' `label` names which, so these must never be pooled,
   * averaged or spread against one another. Pick one model and say which.
   */
  period: ModelSeries[];
  /** primary wave model wave_direction ("from" convention). */
  directionDeg: (number | null)[];
}

/** Per-hour squall risk aligned to `times`: 0 none · 1 possible · 2 likely · 3 severe. */
export interface SquallForecast {
  level: number[];
  why: (string | null)[];
}

/** Surface current (SMOC): speed in knots, direction is "toward" (set). */
export interface CurrentForecast {
  speedKn: (number | null)[];
  dirDeg: (number | null)[];
}

/** Swell / wind-sea partition from the best_match marine model. */
export interface PartitionForecast {
  swellH: (number | null)[] | null;
  swellPer: (number | null)[] | null;
  swellDir: (number | null)[] | null;
  windSeaH: (number | null)[] | null;
  windSeaDir: (number | null)[] | null;
}

/** Seabed relief over a ~5 km box: positive metres below sea level. */
export interface Relief {
  shallow: number;
  deep: number;
  mean: number;
}

export interface PointForecast {
  lat: number;
  lon: number;
  /** hourly local labels, e.g. "2026-07-11T14:00". */
  times: string[];
  /** index of the current hour (>=0), or 0 if unknown. */
  nowIndex: number;
  timezoneAbbr: string;
  utcOffsetSeconds: number;
  wind: WindForecast | null;
  waves: WaveForecast | null;
  /**
   * Per-hour finite ensemble member wind speeds (kn) — the raw arrays the
   * wind quantiles come from, aligned to `times`. A missing ensemble hour is
   * `null`; workability analysis needs these directly.
   */
  ensembleMembers: (number[] | null)[] | null;
  /** GFS visibility, metres, aligned to `times` (first model with data). */
  visibilityM: (number | null)[] | null;
  /** Air temperature (°C) — the DPR's "Air" column. */
  airTempC: (number | null)[] | null;
  /** Total cloud cover (%) — the DPR's "Sky" column. */
  cloudPct: (number | null)[] | null;
  /** Mean sea-level pressure, hPa. */
  pressureHpa: (number | null)[] | null;
  /** Precipitation, mm in the hour. Already fetched for squall discrimination. */
  precipMm: (number | null)[] | null;
  /** Precipitation probability, %. */
  precipProb: (number | null)[] | null;
  /** Ported squall discrimination, aligned to `times`. */
  squall: SquallForecast | null;
  /** GFS wind gusts (kn), aligned to `times` — read out of the squall
   *  diagnostics response, the one call that requests wind_gusts_10m. Null when
   *  that endpoint failed; the gusts were always fetched, just never surfaced
   *  until the conditions strip wanted them. */
  gustsKn: (number | null)[] | null;
  /** Surface current speed (kn) + set direction, aligned to `times`. */
  current: CurrentForecast | null;
  /** Sea-surface temperature (°C), aligned to `times` — one more hourly column
   *  on the best_match marine call that already fetches currents and the
   *  partition, so it costs no extra request. Null when that endpoint failed. */
  sstC: (number | null)[] | null;
  /** Swell / wind-sea partition, aligned to `times`. */
  partition: PartitionForecast | null;
  /** Positive metres below sea level at the point; null if land/unknown. */
  depthM: number | null;
  /** Ground elevation (m) when the point is charted as land (elev >= 0); else null. */
  landElevM: number | null;
  /** Seabed relief within a ~5 km box; null when land/unknown. */
  relief: Relief | null;
}

/** The first model whose series holds any real value. A summary row shows one
 *  line's worth and names the model it took; this is the pick that decides
 *  which. Null when every column is empty. Same convention as the visibility
 *  and wave-direction picks below. (Salvaged from the retired conditions
 *  strip's striplayout.ts, where it was the one genuinely reusable piece.) */
export function firstWithData<T extends { data: (number | null)[] }>(
  models: readonly T[],
): T | null {
  for (const m of models) {
    if (m.data.some((v) => v !== null && Number.isFinite(v))) return m;
  }
  return null;
}

interface ModelSpec {
  key: string;
  label: string;
  slot: string;
}

/**
 * Which period QUANTITY a wave model publishes under Open-Meteo's single
 * `wave_period` name.
 *
 *  - "peak"   — Tp, the period of the spectral peak. The single most energetic
 *               train, and nothing about the rest of the spectrum.
 *  - "energy" — Te = Tm−1,0 = m₋₁/m₀, the spectrally-weighted mean. A whole-sea
 *               average, so a second train drags it toward itself.
 *
 * These are DIFFERENT PHYSICAL QUANTITIES. They are not two estimates of one
 * number, and the gap between them is a property of the sea, not an error bar:
 * in a unimodal swell they nearly coincide, and in a bimodal sea the peak can
 * read half again the energy period for the very same water.
 *
 * This type exists so that the gap cannot be re-introduced silently. It is
 * REQUIRED on every WAVE_MODELS entry (see WaveModelSpec) because the defect it
 * guards against was not a wrong value — it was a missing question. Four models
 * were asked for "wave_period", each answered in its own currency, and the
 * console plotted the four answers on one axis as though they were comparable.
 */
export type PeriodKind = "peak" | "energy";

/**
 * A wave model MUST declare which period quantity it serves.
 *
 * Required, not optional, and deliberately so: an optional field would default
 * a fifth model to whatever the reader assumed, which is exactly how the first
 * four came to be treated as one quantity. With this required, adding a model
 * without classifying it is a type error at the line where the model is added.
 */
interface WaveModelSpec extends ModelSpec {
  periodKind: PeriodKind;
}

const WIND_MODELS: readonly ModelSpec[] = [
  // gfs_seamless is NOT plain GFS: Open-Meteo blends in NCEP's high-resolution
  // models where they exist — over the Gulf the near term is largely HRRR at
  // 3 km. Labelling it "GFS" made it look like the same source as the map's
  // Map·GFS-Wave trace (raw GFS-Wave 0.25°), so their honest disagreement —
  // 3 km physics vs 25 km physics — read as one model contradicting itself,
  // which is far more alarming than two models differing.
  { key: "gfs_seamless", label: "GFS blend", slot: "--m1" },
  { key: "ecmwf_ifs025", label: "ECMWF IFS", slot: "--m2" },
  { key: "icon_seamless", label: "DWD ICON", slot: "--m3" },
];

// Open-Meteo serves ONE field name, `wave_period`, and fills it from whichever
// period each upstream model natively publishes. The name is the same for all
// four; the quantity behind it is not. Mapping verified against Open-Meteo's
// own downloader source (which native variable it reads per model) and
// confirmed empirically — see dev/periodcheck.mjs, the live diagnostic that
// re-measures the split against the one model publishing both.
//
// NOTE ON THE OBVIOUS-LOOKING FIX. There is a published relation between the
// two, Te ≈ 0.79–0.85·Tp for real mixed seas (Ahn et al. 2021, Ocean
// Engineering 228:108937; narrower for idealised spectra — 0.857 Pierson-
// Moskowitz, 0.903 JONSWAP γ=3.3). We do NOT apply it. It is a ±8% band, not a
// coefficient, and running the conversion would turn a measurement into an
// estimate while it kept a measurement's typography — the thing CONVENTIONS.md
// "Errors & honesty" forbids. The diagnostic's own numbers make the point: at
// 35°N 140°W ECMWF's energy period ran 0.713 of its OWN peak period, outside
// that band entirely. So we label instead, and the reader converts or does not,
// knowingly. This is a live question for Dr Bender, NOT a closed one — if he
// wants a converted trace it should arrive as its own clearly-derived series
// with the band shown, never as a silent adjustment to these four.
const WAVE_MODELS: readonly WaveModelSpec[] = [
  // NCEP WAVEWATCH III serves PERPW — "primary wave mean period" in WMO's
  // titling, which is a naming wart: the field is the PEAK period.
  { key: "ncep_gfswave025", label: "GFS-Waves (WW3)", slot: "--m1", periodKind: "peak" },
  // ECMWF WAM serves mwp, the mean wave period Tm−1,0. (This is also the one
  // model that separately publishes `wave_peak_period`, which is what lets the
  // live diagnostic measure the gap within a single model.)
  { key: "ecmwf_wam025", label: "ECMWF WAM", slot: "--m2", periodKind: "energy" },
  // Météo-France MFWAM serves VTM10 — the "T m-1,0" spectral moment period.
  { key: "meteofrance_wave", label: "MFWAM", slot: "--m3", periodKind: "energy" },
  // DWD GWAM serves tm10, the same Tm−1,0 quantity.
  { key: "gwam", label: "DWD GWAM", slot: "--m4", periodKind: "energy" },
];

/**
 * The period quantity each wave model publishes, keyed by model.
 *
 * Exported so that display code names the quantity from the SAME table the
 * fetch is built from, rather than keeping its own copy that can drift. (It
 * had one: panel/rows.ts carried a private `PEAK_PERIOD_MODELS` set while this
 * module stayed silent, so the table told the truth and the chart did not.)
 */
export const WAVE_PERIOD_KIND: ReadonlyMap<string, PeriodKind> = new Map(
  WAVE_MODELS.map((m) => [m.key, m.periodKind]),
);

/** The quantity named as it is printed beside a model: "peak period" etc. */
export function periodKindLabel(kind: PeriodKind): string {
  return kind === "peak" ? "peak period" : "energy period";
}

// --- JSON narrowing helpers -------------------------------------------------
//
// `res.json()` is `unknown`. Rather than assert a shape, we narrow it lazily
// through small guards: is this a plain object? does this key hold an array of
// numbers? Everything the upstream might legitimately omit or null out falls
// back to a benign default, so a partial response degrades one column at a time
// instead of blowing up the whole assembly.

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function hourlyOf(response: unknown): Record<string, unknown> | null {
  const record = asRecord(response);
  return record ? asRecord(record.hourly) : null;
}

function timeArrayOf(hourly: Record<string, unknown> | null): string[] | null {
  if (!hourly) return null;
  const time = hourly.time;
  if (!Array.isArray(time)) return null;
  return time.map((t) => (typeof t === "string" ? t : String(t)));
}

/** Read a hourly variable column as (number | null)[] of the given length. */
function readSeries(
  hourly: Record<string, unknown> | null,
  key: string,
  length: number,
): (number | null)[] {
  const out: (number | null)[] = new Array<number | null>(length).fill(null);
  if (!hourly) return out;
  const raw = hourly[key];
  if (!Array.isArray(raw)) return out;
  for (let i = 0; i < length; i++) {
    const v = raw[i];
    if (typeof v === "number" && Number.isFinite(v)) out[i] = v;
  }
  return out;
}

/** The raw hourly column as an untyped array, or null when absent. */
function rawArray(
  hourly: Record<string, unknown> | null,
  key: string,
): unknown[] | null {
  if (!hourly) return null;
  const raw = hourly[key];
  return Array.isArray(raw) ? raw : null;
}

/** Finite number at index `i` of a raw column, or null. */
function numAt(arr: unknown[] | null, i: number | undefined): number | null {
  if (!arr || i == null) return null;
  const v = arr[i];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Build a time-string index for a marine/atmo hourly block so we can align
 * an upstream that carries its own axis to the label axis (never assume the
 * arrays line up positionally — v1 did this with a Map).
 */
function timeIndex(
  hourly: Record<string, unknown> | null,
): { time: string[]; at: Map<string, number> } | null {
  const time = timeArrayOf(hourly);
  if (!time) return null;
  const at = new Map<string, number>();
  time.forEach((t, j) => {
    if (!at.has(t)) at.set(t, j);
  });
  return { time, at };
}

function utcOffsetOf(response: unknown): number | null {
  const record = asRecord(response);
  if (!record) return null;
  return typeof record.utc_offset_seconds === "number"
    ? record.utc_offset_seconds
    : null;
}

function timezoneAbbrOf(response: unknown): string | null {
  const record = asRecord(response);
  if (!record) return null;
  return typeof record.timezone_abbreviation === "string"
    ? record.timezone_abbreviation
    : null;
}

// --- Statistics -------------------------------------------------------------

/** Linear-interpolation quantile of an ascending-sorted, non-empty array. */
function quantile(sortedAsc: number[], q: number): number {
  const n = sortedAsc.length;
  if (n === 0) return NaN;
  if (n === 1) return sortedAsc[0];
  const pos = q * (n - 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (pos - lo);
}

// --- Ensemble ---------------------------------------------------------------

/**
 * Build p05/p50/p95 wind-speed bands from the ensemble response, aligned to
 * `times`. Members are every hourly column named "wind_speed_10m*" that holds
 * at least one finite value. The ensemble's own time axis need not match
 * `times`, so we map by time-string when we have one.
 */
function buildEnsemble(
  response: unknown,
  times: string[],
): { p05: (number | null)[]; p50: (number | null)[]; p95: (number | null)[] } | null {
  const hourly = hourlyOf(response);
  if (!hourly) return null;

  const members: (number | null)[][] = [];
  for (const key of Object.keys(hourly)) {
    if (!key.startsWith("wind_speed_10m")) continue;
    const raw = hourly[key];
    if (!Array.isArray(raw)) continue;
    const column: (number | null)[] = raw.map((v) =>
      typeof v === "number" && Number.isFinite(v) ? v : null,
    );
    if (column.some((v) => v !== null)) members.push(column);
  }
  if (members.length === 0) return null;

  // Map each output hour to an index into the ensemble arrays. When the
  // ensemble carries its own time axis we resolve by matching time-strings
  // (never assume the arrays line up); otherwise fall back to positional.
  const ensTime = timeArrayOf(hourly);
  let ensIndexFor: (i: number) => number;
  if (ensTime) {
    const byTime = new Map<string, number>();
    ensTime.forEach((t, idx) => {
      if (!byTime.has(t)) byTime.set(t, idx);
    });
    ensIndexFor = (i) => byTime.get(times[i]) ?? -1;
  } else {
    ensIndexFor = (i) => i;
  }

  const p05: (number | null)[] = new Array<number | null>(times.length).fill(null);
  const p50: (number | null)[] = new Array<number | null>(times.length).fill(null);
  const p95: (number | null)[] = new Array<number | null>(times.length).fill(null);

  for (let i = 0; i < times.length; i++) {
    const ei = ensIndexFor(i);
    if (ei < 0) continue;
    const values: number[] = [];
    for (const column of members) {
      const v = column[ei];
      if (v !== null && v !== undefined && Number.isFinite(v)) values.push(v);
    }
    if (values.length === 0) continue;
    values.sort((a, b) => a - b);
    p05[i] = quantile(values, 0.05);
    p50[i] = quantile(values, 0.5);
    p95[i] = quantile(values, 0.95);
  }

  return { p05, p50, p95 };
}

/**
 * Per-hour ensemble member wind speeds, aligned to `times`. Every hourly
 * column named "wind_speed_10m*" is a member; each output hour collects the
 * finite members for that hour. A missing ensemble hour is null; an hour that
 * exists but has no finite members is an empty array. (Ported from v1
 * `alignMembers`.)
 */
function alignEnsembleMembers(
  response: unknown,
  times: string[],
): (number[] | null)[] | null {
  const hourly = hourlyOf(response);
  const index = timeIndex(hourly);
  if (!hourly || !index) return null;
  const keys = Object.keys(hourly).filter((k) => k.startsWith("wind_speed_10m"));
  if (keys.length === 0) return null;
  const columns = keys.map((k) => rawArray(hourly, k));
  return times.map((t) => {
    const j = index.at.get(t);
    if (j == null) return null;
    const vals: number[] = [];
    for (const col of columns) {
      const v = numAt(col, j);
      if (v !== null) vals.push(v);
    }
    return vals;
  });
}

// --- Squall discrimination --------------------------------------------------
//
// "Don't trust the mean line here." CAPE alone can't discriminate in the
// tropics (GoM summer background is 2000-3500 J/kg), so it is only a fuel gate.
// The discriminators: CIN (is the cap open), the model's own convective
// precipitation (its convection scheme actually firing), and gust-minus-mean
// spread — the WMO squall definition is a 16 kn rise to a sustained 22 kn.
// Constants synthesized from published bands, unverified as a combined rule —
// hindcast calibration against vessel logs is Les's. (Ported verbatim from v1.)

const SQUALL_RULE = {
  fuel: 800, // J/kg — below tropical maritime background; gates only truly stable regimes
  cinOpen: 50,
  cinCap: 100, // |CIN| J/kg — cap open / cap suppressing
  trig: 0.2,
  active: 1.0,
  org: 5, // mm/h convective precip: scheme active / real shower / organized system
  orgCape: 2000, // J/kg floor for the organized-system severe path
  ppTrig: 40, // % precipitation probability — ensemble-agreement trigger
  spreadSoft: 10,
  spreadWMO: 16, // kn gust-minus-mean; 16 = WMO squall rise criterion
  gustFloor: 22,
  gustSevere: 34, // kn — WMO sustained floor / near-gale gust = severe outright
} as const;

/**
 * Per-hour squall level (0-3) + a "why" tooltip string, aligned to `times`.
 * GFS-only response — the one model exposing the full convective set.
 */
function squallLevels(
  response: unknown,
  times: string[],
): { level: number[]; why: (string | null)[] } | null {
  const h = hourlyOf(response);
  const index = timeIndex(h);
  const cape = rawArray(h, "cape");
  if (!h || !index || !cape) return null;

  const cin = rawArray(h, "convective_inhibition");
  const showers = rawArray(h, "showers");
  const precip = rawArray(h, "precipitation");
  const rain = rawArray(h, "rain");
  const precipProb = rawArray(h, "precipitation_probability");
  const gusts = rawArray(h, "wind_gusts_10m");
  const wspd = rawArray(h, "wind_speed_10m");
  const R = SQUALL_RULE;

  const level: number[] = [];
  const why: (string | null)[] = [];
  for (const t of times) {
    const i = index.at.get(t);
    const capeV = numAt(cape, i);
    if (capeV === null) {
      level.push(0);
      why.push(null);
      continue;
    }
    const cinV = Math.abs(numAt(cin, i) ?? 0);
    const showersV = numAt(showers, i);
    const precipV = numAt(precip, i);
    const rainV = numAt(rain, i);
    // convective precip: showers if present, else precipitation minus stratiform rain
    let cp: number;
    if (showersV !== null) cp = showersV;
    else if (precipV !== null && rainV !== null) cp = Math.max(0, precipV - rainV);
    else if (precipV !== null) cp = precipV;
    else cp = 0;
    const pp = numAt(precipProb, i);
    const gust = numAt(gusts, i) ?? 0;
    const spread = gust - (numAt(wspd, i) ?? gust);

    const fuel = capeV >= R.fuel;
    const trigger = cp >= R.trig || (pp !== null && pp >= R.ppTrig);
    const active = cp >= R.active;
    const gustFlag = spread >= R.spreadSoft && gust >= R.gustFloor;
    const squallGust =
      (spread >= R.spreadWMO && gust >= R.gustFloor) || gust >= R.gustSevere;

    let L = 0;
    if ((active && squallGust) || (cp >= R.org && capeV >= R.orgCape)) L = 3;
    else if (fuel && cinV < R.cinCap && (active || (trigger && gustFlag))) L = 2;
    else if (fuel && cinV < R.cinOpen && trigger) L = 1;
    level.push(L);
    why.push(
      L === 0
        ? null
        : (L === 3
            ? "severe squall signal"
            : L === 2
              ? "squalls likely"
              : "squalls possible") +
            ": " +
            [
              cp >= R.trig ? `convective precip ${cp.toFixed(1)} mm/h` : null,
              spread >= R.spreadSoft
                ? `gusts ${Math.round(gust)} kn (+${Math.round(spread)} over mean)`
                : null,
              pp !== null && pp >= R.ppTrig && cp < R.trig
                ? `precip probability ${pp} %`
                : null,
              `CAPE ${Math.round(capeV)}, CIN ${Math.round(cinV)} J/kg`,
            ]
              .filter((s): s is string => s !== null)
              .join(", "),
    );
  }
  return { level, why };
}

/** wind_gusts_10m aligned to the label axis (kn — the squall URL asks for
 *  knots), or null when the response is unusable. */
/**
 * Any column of the squall (gfs_seamless) response, aligned to `times`.
 *
 * That call already asks for precipitation, rain, showers and the probability —
 * it has since squall discrimination was built — and until now every one of
 * them was read once to decide a boolean and then thrown away. The general
 * weather block is mostly a matter of keeping what we already fetch.
 */
function squallSeries(
  response: unknown,
  times: string[],
  key: string,
): (number | null)[] | null {
  const h = hourlyOf(response);
  const index = timeIndex(h);
  const col = rawArray(h, key);
  if (!index || !col) return null;
  return times.map((t) => numAt(col, index.at.get(t)));
}

function gustSeries(response: unknown, times: string[]): (number | null)[] | null {
  const h = hourlyOf(response);
  const index = timeIndex(h);
  const gusts = rawArray(h, "wind_gusts_10m");
  if (!index || !gusts) return null;
  return times.map((t) => numAt(gusts, index.at.get(t)));
}

// --- Currents + swell/wind-sea partition (best_match marine) ----------------

/**
 * Build an aligner for a best_match marine response: given a variable key,
 * return that column aligned to `times` (by matching time strings), or null
 * when the key is absent. (Ported from v1 `alignCur`.)
 */
function marineAligner(
  response: unknown,
  times: string[],
): (key: string) => (number | null)[] | null {
  const hourly = hourlyOf(response);
  const index = timeIndex(hourly);
  if (!hourly || !index) return () => null;
  return (key: string) => {
    const arr = rawArray(hourly, key);
    if (!arr) return null;
    return times.map((t) => numAt(arr, index.at.get(t)));
  };
}

// --- NCEI bathymetry --------------------------------------------------------

/**
 * NCEI identify returns ground/seafloor elevation as a string in `value`
 * (negative = below sea level). Returns the elevation in metres, or null.
 */
function parseIdentifyElev(response: unknown): number | null {
  const record = asRecord(response);
  if (!record) return null;
  const v = record.value;
  const n =
    typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * NCEI computeStatisticsHistograms → seabed relief. Elevations are negative
 * below sea level, converted to positive depths. Only when the box actually
 * covers seafloor (min < 0). (Ported from v1.)
 */
function parseRelief(response: unknown): Relief | null {
  const record = asRecord(response);
  if (!record) return null;
  const stats = record.statistics;
  if (!Array.isArray(stats) || stats.length === 0) return null;
  const s = asRecord(stats[0]);
  if (!s) return null;
  const { min, max, mean } = s;
  if (
    typeof min === "number" &&
    typeof max === "number" &&
    typeof mean === "number" &&
    Number.isFinite(min) &&
    Number.isFinite(max) &&
    Number.isFinite(mean) &&
    min < 0
  ) {
    return { shallow: Math.max(0, -max), deep: -min, mean: -mean };
  }
  return null;
}

// --- Time ------------------------------------------------------------------

/**
 * Index of the hour closest to now. Open-Meteo times are local wall-clock
 * strings ("2026-07-11T14:00"); we reconstruct their true UTC epoch using the
 * response's utc_offset_seconds, then pick the nearest to Date.now().
 */
function computeNowIndex(times: string[], utcOffsetSeconds: number): number {
  const now = Date.now();
  let best = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < times.length; i++) {
    const epochUtc = Date.parse(times[i] + ":00Z") - utcOffsetSeconds * 1000;
    if (Number.isNaN(epochUtc)) continue;
    const diff = Math.abs(epochUtc - now);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return best < 0 ? 0 : best;
}

// --- Fetch -----------------------------------------------------------------

async function getJson(url: string): Promise<unknown> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  return (await res.json()) as unknown;
}

function valueOf(result: PromiseSettledResult<unknown>): unknown {
  return result.status === "fulfilled" ? result.value : null;
}

// --- Public API ------------------------------------------------------------

export async function fetchPointForecast(
  lat: number,
  lon: number,
): Promise<PointForecast> {
  const base =
    `latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}` +
    `&timezone=auto&forecast_days=7&past_days=2`;

  // These five go through OUR server (server/openmeteo.go), not to Open-Meteo
  // directly. The professional subscription is authenticated by a key, and a key
  // this file could hold is a key any visitor could read out of the bundle — so
  // the key stays on the server and the browser asks us. Same-origin, so the
  // session cookie rides along on fetch's default credentials mode.
  //
  // The NCEI bathymetry call below stays direct: it is public, CORS-enabled, and
  // relaying it would put load on us for nothing.
  const windUrl =
    "/api/om/forecast?" +
    base +
    // temperature_2m and cloud_cover ride along on a call already being made:
    // they are the "Air" and "Sky" columns of the Daily Progress Report's 0700
    // weather summary (docs/DELIVERABLES.md §2.0), which is the form this
    // console's report is written to be pasted into.
    "&hourly=wind_speed_10m,wind_direction_10m,visibility,temperature_2m,cloud_cover" +
    // pressure_msl: the oldest synoptic instrument there is, and the one part of
    // "what is the weather doing" that needs no interpretation — a mariner reads
    // a falling barometer without anybody's discussion attached to it.
    ",pressure_msl" +
    "&wind_speed_unit=kn&models=" +
    WIND_MODELS.map((m) => m.key).join(",");

  const waveUrl =
    "/api/om/marine?" +
    base +
    "&hourly=wave_height,wave_direction,wave_period&models=" +
    WAVE_MODELS.map((m) => m.key).join(",");

  const ensembleUrl =
    "/api/om/ensemble?" +
    base +
    "&hourly=wind_speed_10m&wind_speed_unit=kn&models=gfs025";

  // Squall diagnostics — GFS only, the one model exposing the full convective set.
  const squallUrl =
    "/api/om/forecast?" +
    base +
    "&hourly=cape,convective_inhibition,precipitation_probability,showers," +
    "precipitation,rain,wind_speed_10m,wind_gusts_10m&wind_speed_unit=kn&models=gfs_seamless";

  // best_match marine call: currents (SMOC) + swell/wind-sea partition — neither
  // exists in the per-model wave requests.
  const currentUrl =
    "/api/om/marine?" +
    base +
    "&hourly=ocean_current_velocity,ocean_current_direction,sea_surface_temperature" +
    ",swell_wave_height,swell_wave_period,swell_wave_direction,wind_wave_height,wind_wave_direction";

  // Seafloor depth (NOAA NCEI global DEM) — CORS-enabled, so no proxy needed.
  const NCEI =
    "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer";
  // NCEI identify returns elevation as a string in `value` (negative = below sea level).
  const depthUrl =
    NCEI +
    "/identify?" +
    `geometry=${encodeURIComponent(JSON.stringify({ x: lon, y: lat }))}` +
    "&geometryType=esriGeometryPoint&sr=4326&returnGeometry=false&returnCatalogItems=false&f=json";
  // Seabed relief: min/max/mean depth over a ~5 km box around the point.
  const BOX = 0.05; // deg half-width ≈ 5 km
  const bathyUrl =
    NCEI +
    "/computeStatisticsHistograms?geometryType=esriGeometryEnvelope&f=json&geometry=" +
    encodeURIComponent(
      JSON.stringify({
        xmin: lon - BOX,
        ymin: lat - BOX,
        xmax: lon + BOX,
        ymax: lat + BOX,
        spatialReference: { wkid: 4326 },
      }),
    );

  const [
    windResult,
    waveResult,
    ensembleResult,
    squallResult,
    currentResult,
    depthResult,
    bathyResult,
  ] = await Promise.allSettled([
    getJson(windUrl),
    getJson(waveUrl),
    getJson(ensembleUrl),
    getJson(squallUrl),
    getJson(currentUrl),
    getJson(depthUrl),
    getJson(bathyUrl),
  ]);

  const windResponse = valueOf(windResult);
  const waveResponse = valueOf(waveResult);
  const ensembleResponse = valueOf(ensembleResult);
  const squallResponse = valueOf(squallResult);
  const currentResponse = valueOf(currentResult);
  const depthResponse = valueOf(depthResult);
  const bathyResponse = valueOf(bathyResult);

  const windHourly = hourlyOf(windResponse);
  const waveHourly = hourlyOf(waveResponse);

  // Health, recorded from the outcomes just gathered rather than probed
  // separately. "forecast" is the deterministic wind+wave pair the charts stand
  // on; the ensemble is its own row because it fails independently and degrades
  // the console differently (spread band and workability probability go, the
  // central lines stay).
  const missing: string[] = [];
  if (windResponse === null) missing.push("wind");
  if (waveResponse === null) missing.push("marine");
  if (squallResponse === null) missing.push("squall");
  reportSystem("forecast", windResponse !== null || waveResponse !== null, {
    note: missing.length > 0 ? `${missing.join("+")} endpoint failed` : "",
  });
  reportSystem("ensemble", ensembleResponse !== null);

  // The wind response owns the canonical time axis; the marine response is the
  // fallback. With neither, there is nothing to plot.
  const windTimes = timeArrayOf(windHourly);
  const times = windTimes ?? timeArrayOf(waveHourly);
  if (!times) throw new Error("no forecast data");

  // Timezone metadata comes from whichever response provided the axis.
  const axisResponse = windTimes ? windResponse : waveResponse;
  const utcOffsetSeconds = utcOffsetOf(axisResponse) ?? 0;
  const timezoneAbbr = timezoneAbbrOf(axisResponse) ?? "";

  let wind: WindForecast | null = null;
  if (windHourly) {
    const models: ModelSeries[] = WIND_MODELS.map((m) => ({
      key: m.key,
      label: m.label,
      slot: m.slot,
      data: readSeries(windHourly, `wind_speed_10m_${m.key}`, times.length),
    }));
    const directionDeg = readSeries(
      windHourly,
      "wind_direction_10m_gfs_seamless",
      times.length,
    );
    wind = {
      models,
      directionDeg,
      ensemble: buildEnsemble(ensembleResponse, times),
    };
  }

  let waves: WaveForecast | null = null;
  if (waveHourly) {
    const height: ModelSeries[] = WAVE_MODELS.map((m) => ({
      key: m.key,
      label: m.label,
      slot: m.slot,
      data: readSeries(waveHourly, `wave_height_${m.key}`, times.length),
    }));
    // The period series carries its QUANTITY in its label, composed here rather
    // than at each display site. Everything that renders a ModelSeries renders
    // `.label` — the chart's line names, the readout strip, the source line
    // under the plot, the conditions table's row note — so naming the quantity
    // once, where the quantity is actually known, is what makes it impossible
    // to draw one of these lines unlabelled. A display site cannot forget a
    // caveat it never had to remember.
    //
    // Height deliberately keeps the plain model name: significant wave height
    // IS the same quantity in all four, and a caveat there would be noise.
    const period: ModelSeries[] = WAVE_MODELS.map((m) => ({
      key: m.key,
      label: `${m.label} ${periodKindLabel(m.periodKind)}`,
      slot: m.slot,
      data: readSeries(waveHourly, `wave_period_${m.key}`, times.length),
    }));

    // First wave model whose direction column has any real value wins.
    let directionDeg: (number | null)[] = new Array<number | null>(
      times.length,
    ).fill(null);
    for (const m of WAVE_MODELS) {
      const dir = readSeries(waveHourly, `wave_direction_${m.key}`, times.length);
      if (dir.some((v) => v !== null)) {
        directionDeg = dir;
        break;
      }
    }

    waves = { height, period, directionDeg };
  }

  // Visibility: first wind model whose column has any real value, in metres.
  let visibilityM: (number | null)[] | null = null;
  if (windHourly) {
    for (const m of WIND_MODELS) {
      const vis = readSeries(windHourly, `visibility_${m.key}`, times.length);
      if (vis.some((v) => v !== null)) {
        visibilityM = vis;
        break;
      }
    }
  }

  // Air temperature (°C) and cloud cover (%), same first-model-with-data rule as
  // visibility above — the DPR's Air and Sky columns.
  const pickFirst = (field: string): (number | null)[] | null => {
    if (!windHourly) return null;
    for (const m of WIND_MODELS) {
      const col = readSeries(windHourly, `${field}_${m.key}`, times.length);
      if (col.some((v) => v !== null)) return col;
    }
    return null;
  };
  const airTempC = pickFirst("temperature_2m");
  const cloudPct = pickFirst("cloud_cover");
  const pressureHpa = pickFirst("pressure_msl");

  const ensembleMembers = alignEnsembleMembers(ensembleResponse, times);
  const squall = squallLevels(squallResponse, times);
  const gustsKn = gustSeries(squallResponse, times);
  const precipMm = squallSeries(squallResponse, times, "precipitation");
  const precipProb = squallSeries(squallResponse, times, "precipitation_probability");

  // Currents + swell/wind-sea partition, aligned to the label axis.
  const alignMarine = marineAligner(currentResponse, times);
  const currentVel = alignMarine("ocean_current_velocity");
  const currentDir = alignMarine("ocean_current_direction");
  let current: CurrentForecast | null = null;
  if (currentVel || currentDir) {
    // velocity km/h → kn (×0.539957); direction is "toward" (set)
    current = {
      speedKn: currentVel
        ? currentVel.map((v) => (v === null ? null : v * 0.539957))
        : new Array<number | null>(times.length).fill(null),
      dirDeg: currentDir ?? new Array<number | null>(times.length).fill(null),
    };
  }

  const sstC = alignMarine("sea_surface_temperature");

  const swellH = alignMarine("swell_wave_height");
  const swellPer = alignMarine("swell_wave_period");
  const swellDir = alignMarine("swell_wave_direction");
  const windSeaH = alignMarine("wind_wave_height");
  const windSeaDir = alignMarine("wind_wave_direction");
  const partition: PartitionForecast | null =
    swellH || swellPer || swellDir || windSeaH || windSeaDir
      ? { swellH, swellPer, swellDir, windSeaH, windSeaDir }
      : null;

  // NCEI depth: elevation < 0 → positive depth below sea level; >= 0 → land.
  const elev = parseIdentifyElev(depthResponse);
  const depthM = elev !== null && elev < 0 ? -elev : null;
  const landElevM = elev !== null && elev >= 0 ? elev : null;
  const relief = parseRelief(bathyResponse);

  return {
    lat,
    lon,
    times,
    nowIndex: computeNowIndex(times, utcOffsetSeconds),
    timezoneAbbr,
    utcOffsetSeconds,
    wind,
    waves,
    ensembleMembers,
    visibilityM,
    airTempC,
    cloudPct,
    pressureHpa,
    precipMm,
    precipProb,
    squall,
    gustsKn,
    current,
    sstC,
    partition,
    depthM,
    landElevM,
    relief,
  };
}
